import { DIGITAL_RUBRIC, FIELD_LABELS, isYesValue } from "./greatDigitalShared";

export const YES_NO_OPTIONS = ["Yes", "No"];

export const GREAT_PASS_FAIL_THRESHOLD = 80;

/** Section caps (match stored DB totals). Sum = 103. */
export const DIGITAL_SECTION_KEYS = {
  Greet: ["greeting_salutation", "professional_tone"],
  Resolve: [
    "identification_verification",
    "demonstrates_ownership",
    "appropriate_responses",
    "questioning",
    "checking_confirming_understanding",
    "clear_concise_explanation_fcr",
  ],
  Engage: ["empathy", "right_recipient", "grammar", "punctuation", "structure_formatting"],
  Assure: ["sounds_reassuring", "confidence"],
  Thank: ["closing", "offers_additional_assistance"],
};

/** Points when answer is Yes (No = 0). Section sums: G=10, R=35, E=38, A=13, T=7. */
export const DIGITAL_RUBRIC_WEIGHTS = {
  greeting_salutation: 3,
  professional_tone: 3,
  identification_verification: 4,
  demonstrates_ownership: 8,
  appropriate_responses: 7,
  questioning: 7,
  checking_confirming_understanding: 6,
  clear_concise_explanation_fcr: 7,
  empathy: 8,
  right_recipient: 8,
  grammar: 7,
  punctuation: 7,
  structure_formatting: 8,
  sounds_reassuring: 7,
  confidence: 6,
  closing: 2,
  offers_additional_assistance: 5,
};

const SECTION_TO_TOTAL_KEY = {
  Greet: "greet_total",
  Resolve: "resolve_total",
  Engage: "engage_total",
  Assure: "assure_total",
  Thank: "thank_total",
};

export const computeDigitalTotalsFromForm = (formData) => {
  let total = 0;
  const totals = {};
  DIGITAL_RUBRIC.forEach(({ section, fields }) => {
    let pts = 0;
    fields.forEach((k) => {
      if (isYesValue(formData[k])) pts += DIGITAL_RUBRIC_WEIGHTS[k] ?? 0;
    });
    const key = SECTION_TO_TOTAL_KEY[section];
    if (key) totals[key] = (Math.round(pts * 100) / 100).toFixed(2);
    total += pts;
  });
  totals.total_score = (Math.round(total * 100) / 100).toFixed(2);
  return totals;
};

export const passFailFromTotalScore = (totalScore) => {
  const score = parseFloat(totalScore);
  if (Number.isNaN(score)) return null;
  return score >= GREAT_PASS_FAIL_THRESHOLD ? "Pass" : "Fail";
};

export const applyPassFailFromTotal = (formData) => {
  const pass_fail = passFailFromTotalScore(formData.total_score);
  return pass_fail ? { ...formData, pass_fail } : formData;
};

export const applyDigitalRubricRecalc = (formData) =>
  applyPassFailFromTotal({ ...formData, ...computeDigitalTotalsFromForm(formData) });

const RUBRIC_FIELD_KEYS = DIGITAL_RUBRIC.flatMap((s) => s.fields);

export const DIGITAL_RUBRIC_FIELD_KEYS = new Set(RUBRIC_FIELD_KEYS);

export const DIGITAL_EDIT_SECTIONS = [
  { id: "info", title: "Interaction Information" },
  { id: "scores", title: "Overall Score" },
  { id: "greet", title: "G — Greet" },
  { id: "resolve", title: "R — Resolve" },
  { id: "engage", title: "E — Engage" },
  { id: "assure", title: "A — Assure" },
  { id: "thank", title: "T — Thank" },
  { id: "totals", title: "Component Totals" },
  { id: "extra", title: "Additional Details" },
];

const sectionIdForRubric = (sectionName) => {
  const map = { Greet: "greet", Resolve: "resolve", Engage: "engage", Assure: "assure", Thank: "thank" };
  return map[sectionName] || "info";
};

const rubricEditableFields = DIGITAL_RUBRIC.flatMap(({ section, fields }) =>
  fields.map((key) => ({
    key,
    label: FIELD_LABELS[key] || key,
    type: "select",
    options: YES_NO_OPTIONS,
    section: sectionIdForRubric(section),
  }))
);

/** Columns admins may update (matches great_digital_data). */
export const DIGITAL_EDITABLE_FIELDS = [
  { key: "advisor_name", label: "Representative", type: "text", section: "info" },
  { key: "interaction_date", label: "Interaction Date", type: "date", section: "info" },
  { key: "interaction_time", label: "Interaction Time", type: "text", section: "info" },
  { key: "supervisor", label: "Supervisor", type: "text", section: "info" },
  { key: "retailer_account_sq_no", label: "Retailer Account SQ No", type: "text", section: "info" },
  { key: "reason_for_interaction", label: "Reason for Interaction", type: "text", section: "info" },
  {
    key: "sentiment",
    label: "Sentiment",
    type: "select",
    options: ["positive", "neutral", "negative"],
    section: "info",
  },
  { key: "sentiment_reason", label: "Sentiment Reason", type: "text", section: "info" },
  { key: "rep_from", label: "Rep From", type: "text", section: "info" },
  { key: "rep_to", label: "Rep To", type: "text", section: "info" },
  { key: "customer_from", label: "Customer From", type: "text", section: "info" },
  { key: "customer_to", label: "Customer To", type: "text", section: "info" },
  { key: "duration_minutes", label: "Duration (minutes)", type: "number", section: "info" },
  { key: "total_score", label: "Total Score", type: "number", section: "scores" },
  { key: "pass_fail", label: "Pass/Fail", type: "select", options: ["Pass", "Fail"], section: "scores" },
  { key: "grade", label: "Grade", type: "text", section: "scores" },
  ...rubricEditableFields,
  { key: "greet_total", label: "Greet Total", type: "number", section: "totals" },
  { key: "resolve_total", label: "Resolve Total", type: "number", section: "totals" },
  { key: "engage_total", label: "Engage Total", type: "number", section: "totals" },
  { key: "assure_total", label: "Assure Total", type: "number", section: "totals" },
  { key: "thank_total", label: "Thank Total", type: "number", section: "totals" },
  { key: "performance_snapshot", label: "Performance Snapshot", type: "textarea", section: "extra" },
  { key: "transcript", label: "Transcript", type: "textarea", section: "extra" },
];

export const DIGITAL_EDITABLE_KEYS = new Set(DIGITAL_EDITABLE_FIELDS.map((f) => f.key));

export const formatDigitalCellValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  return String(value);
};
